import { useEffect, useRef, useState, useCallback } from "react";

export const useMessagesScroll = (messagesQuery) => {
  const messagesContainerRef = useRef(null);
  const [canPaginate, setCanPaginate] = useState(false);
  const [hasScrolledBottom, setHasScrolledBottom] = useState(false);
  const lastScrollTop = useRef(0);
  const isInitialMount = useRef(true);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = messagesContainerRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        const maxScroll = el.scrollHeight - el.clientHeight;
        el.scrollTo({ top: maxScroll, behavior });
        setHasScrolledBottom(true);
        lastScrollTop.current = maxScroll;
      }, 30);
    });
  }, []);


  useEffect(() => {
    if (hasScrolledBottom) {
      const timer = setTimeout(() => setCanPaginate(true), 300);
      return () => clearTimeout(timer);
    }
  }, [hasScrolledBottom]);

  useEffect(() => {
    const firstPage = messagesQuery?.data?.pages?.[0];
    const hasItems = firstPage?.items?.length > 0;

    if (hasItems && !hasScrolledBottom && isInitialMount.current) {
      scrollToBottom("auto");
      isInitialMount.current = false;
    }
  }, [messagesQuery?.data, hasScrolledBottom, scrollToBottom]);

  const handleScroll = useCallback(
    (e) => {
      if (!canPaginate) return;

      const el = e.currentTarget;
      const currentScrollTop = el.scrollTop;
      const isScrollingUp = currentScrollTop < lastScrollTop.current;

      if (
        currentScrollTop <= 40 &&
        isScrollingUp &&
        messagesQuery.hasNextPage &&
        !messagesQuery.isFetchingNextPage
      ) {
        const previousScrollHeight = el.scrollHeight;

        messagesQuery.fetchNextPage().then(() => {
          requestAnimationFrame(() => {
            if (el) {
              const newScrollHeight = el.scrollHeight;
              const scrollDiff = newScrollHeight - previousScrollHeight;
              el.scrollTop = currentScrollTop + scrollDiff;
            }
          });
        });
      }

      lastScrollTop.current = currentScrollTop;
    },
    [canPaginate, messagesQuery]
  );

  return { messagesContainerRef, handleScroll, scrollToBottom };
};